const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

async function safeJson(res) {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

/**
 * Upload new document file under a requirement
 */
export async function uploadDossierDocument({
  requirementId,
  file,
  accessToken,
  documentGroup = "",
  displayName = "",
}) {
  const formData = new FormData();
  formData.append("requirement_id", requirementId);
  formData.append("file", file);

  if (documentGroup) {
    formData.append("document_group", documentGroup);
  }

  if (displayName) {
    formData.append("display_name", displayName);
  }

  const res = await fetch(`${API_BASE}/api/homeowner/documents/upload/`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    body: formData,
  });

  const data = await safeJson(res);

  if (!res.ok) {
    console.error("Upload document failed:", {
      status: res.status,
      data,
    });

    throw new Error(
      data?.detail ||
        data?.error ||
        data?.non_field_errors?.[0] ||
        data?.file?.[0] ||
        data?.requirement_id?.[0] ||
        "Failed to upload document.",
    );
  }

  return data;
}

/**
 * Upload a new version for an existing document file
 */
export async function uploadDossierDocumentNewVersion({
  fileId,
  file,
  accessToken,
}) {
  const formData = new FormData();
  formData.append("file", file);

  const res = await fetch(
    `${API_BASE}/api/homeowner/documents/files/${fileId}/new-version/`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: formData,
    },
  );

  const data = await safeJson(res);

  if (!res.ok) {
    throw new Error(
      data?.detail || data?.error || "Failed to upload new document version.",
    );
  }

  return data;
}

/**
 * Remove one uploaded document file
 */
export async function removeDossierDocumentFile({ fileId, accessToken }) {
  const res = await fetch(
    `${API_BASE}/api/homeowner/documents/files/${fileId}/remove/`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );

  const data = await safeJson(res);

  if (!res.ok) {
    throw new Error(
      data?.detail || data?.error || "Failed to remove document file.",
    );
  }

  return data;
}

/**
 * Remove all files under a document requirement
 * Optional compatibility function.
 */
export async function removeDossierDocument({ requirementId, accessToken }) {
  const res = await fetch(
    `${API_BASE}/api/homeowner/documents/${requirementId}/remove/`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );

  const data = await safeJson(res);

  if (!res.ok) {
    throw new Error(
      data?.detail || data?.error || "Failed to remove document.",
    );
  }

  return data;
}

/**
 * Start extraction
 */
export async function startDossierExtraction({
  dossierId,
  dossierApplicantId,
  accessToken,
}) {
  const res = await fetch(
    `${API_BASE}/api/homeowner/dossiers/${dossierId}/start-extraction/`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        dossier_applicant_id: dossierApplicantId,
      }),
    },
  );

  const data = await safeJson(res);

  if (!res.ok) {
    throw new Error(
      data?.detail ||
        data?.missing_required_documents?.[0]?.document_label ||
        "Failed to start extraction.",
    );
  }

  const jobId =
    data?.job_id || data?.id || data?.jobId || data?.extraction_job_id || null;

  if (!jobId) {
    console.error("Extraction response missing job id:", data);
    throw new Error("Extraction started but no job id returned.");
  }

  return {
    job_id: jobId,
  };
}

/**
 * Get extraction job status
 */
export async function getExtractionJobStatus({ jobId, accessToken }) {
  const res = await fetch(
    `${API_BASE}/api/homeowner/extraction-jobs/${jobId}/`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );

  const data = await safeJson(res);

  if (!res.ok) {
    throw new Error(data?.detail || "Failed to fetch extraction job status.");
  }

  return data;
}

/**
 * Get extracted data
 */
export async function getApplicantExtractedData({
  dossierId,
  dossierApplicantId,
  accessToken,
}) {
  const res = await fetch(
    `${API_BASE}/api/homeowner/dossiers/${dossierId}/applicants/${dossierApplicantId}/extracted-data/`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );

  const data = await safeJson(res);

  if (!res.ok) {
    throw new Error(data?.detail || "Failed to fetch extracted data.");
  }

  return data;
}

/**
 * Fetch document version as blob for preview
 */
export async function fetchDossierDocumentFileBlob({ versionId, accessToken }) {
  const res = await fetch(
    `${API_BASE}/api/homeowner/documents/versions/${versionId}/file/`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );

  if (!res.ok) {
    const error = await safeJson(res);
    throw new Error(error?.detail || "Failed to fetch document preview.");
  }

  return res.blob();
}
